import type {
  AlertRule,
  AlertRuleInput,
  EscalationChain,
  EscalationChainInput,
  RecipientGroup,
  RecipientGroupInput,
  SdtWindow,
  SdtWindowInput,
} from '@detect/shared';
import { api, withTenant, type PaginatedResult } from './api';
import type { WithId } from './types';

/** Alerting configuration surfaces (M5): rules, chains, recipient
 *  groups, SDT windows (SDT is create/delete only — windows are
 *  immutable once cut). */

function crud<T, TInput>(base: string) {
  return {
    list: (
      tenantId: string,
      params: { limit?: number; startAfter?: string } = {}
    ): Promise<PaginatedResult<WithId<T>>> =>
      api.getPaginated<WithId<T>>(withTenant(`/alerting/${base}`, tenantId, params)),
    get: (tenantId: string, id: string): Promise<WithId<T>> =>
      api.get<WithId<T>>(withTenant(`/alerting/${base}/${id}`, tenantId)),
    create: (tenantId: string, input: TInput): Promise<WithId<T>> =>
      api.post<WithId<T>>(withTenant(`/alerting/${base}`, tenantId), input),
    update: (
      tenantId: string,
      id: string,
      patch: Record<string, unknown>
    ): Promise<WithId<T>> =>
      api.patch<WithId<T>>(withTenant(`/alerting/${base}/${id}`, tenantId), patch),
    remove: (tenantId: string, id: string): Promise<void> =>
      api.delete(withTenant(`/alerting/${base}/${id}`, tenantId)),
  };
}

export const ruleService = crud<AlertRule, AlertRuleInput>('rules');
export const chainService = crud<EscalationChain, EscalationChainInput>('chains');
export const recipientGroupService = crud<RecipientGroup, RecipientGroupInput>(
  'recipient-groups'
);

export const sdtService = {
  list: (
    tenantId: string,
    params: { limit?: number; startAfter?: string } = {}
  ): Promise<PaginatedResult<WithId<SdtWindow>>> =>
    api.getPaginated<WithId<SdtWindow>>(withTenant('/alerting/sdt', tenantId, params)),
  create: (tenantId: string, input: SdtWindowInput): Promise<WithId<SdtWindow>> =>
    api.post<WithId<SdtWindow>>(withTenant('/alerting/sdt', tenantId), input),
  remove: (tenantId: string, id: string): Promise<void> =>
    api.delete(withTenant(`/alerting/sdt/${id}`, tenantId)),
};
